import { Icon, TextField } from '@equinor/eds-core-react';
import { error_filled } from '@equinor/eds-icons';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  ScenarioTemplateParameters,
  useScenarioStore,
} from '../stores/ScenarioStore';
/* eslint-disable camelcase */
/* eslint-disable react/prop-types */
Icon.add({ error_filled });

/** Normalize comma-decimal input (e.g. "0,5" → 0.5) to a number */
const normalizeNumericValue = (value: string | number): number => {
  if (typeof value === 'number') return value;
  return parseFloat(String(value).replace(',', '.'));
};

interface Props {
  variable: variable;
  onSubmit?: (value: React.SetStateAction<string | number | undefined>) => void;
}

type validators = { min?: number; max?: number; allowedValues: number[] };
type variable = {
  name: string;
  validators: validators;
  units: string;
  type: string;
  id: string;
  default: number;
  factor: boolean;
};

export const ValueInputMinMax = ({ variable }: Props) => {
  const updateParameter = useScenarioStore((state) => state.updateParameter);
  const setValidationError = useScenarioStore(
    (state) => state.setValidationError,
  );
  const clearValidationError = useScenarioStore(
    (state) => state.clearValidationError,
  );
  const { currentTemplate } = useScenarioStore();
  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      data: variable.default,
    },
  });

  // Reset form when variable.default changes (e.g., when a new template is loaded)
  useEffect(() => {
    reset({ data: variable.default });
  }, [variable.default, reset, currentTemplate]);

  // Sync validation errors to the store
  useEffect(() => {
    if (errors.data) {
      setValidationError(variable.id, errors.data.message ?? 'Invalid value');
    } else {
      clearValidationError(variable.id);
    }
  }, [errors.data, variable.id, setValidationError, clearValidationError]);

  // Clean up on unmount
  useEffect(() => {
    return () => clearValidationError(variable.id);
  }, [variable.id, clearValidationError]);

  const handleFormSubmit = (data: { data: number }) => {
    const numValue = normalizeNumericValue(data.data);
    updateParameter(variable.id as keyof ScenarioTemplateParameters, numValue);
  };

  return (
    <form
      onChange={handleSubmit(handleFormSubmit)}
      style={{ display: 'flex', flexDirection: 'row', gap: '1rem' }}
    >
      <Controller
        name="data"
        control={control}
        rules={{
          required: 'Enter value within range',
          validate: (value) => {
            const num = normalizeNumericValue(value);
            if (isNaN(num)) return 'Enter a valid number';
            const min = variable.validators.min;
            const max = variable.validators.max;
            if (min != null && num < min)
              return `Enter value of at least ${min}`;
            if (max != null && num > max)
              return `Enter value of at most ${max}`;
            return true;
          },
        }}
        render={({ field: { ref, ...props }, fieldState: { error } }) => (
          <TextField
            {...props}
            id={props.name}
            inputRef={ref}
            inputIcon={
              error ? <Icon data={error_filled} title="error" /> : undefined
            }
            helperText={error?.message}
            variant={error ? 'error' : undefined}
            style={{
              minWidth: '60px',
              maxWidth: '140px',
              alignContent: 'center',
            }}
          />
        )}
      />
    </form>
  );
};

export const ValueInputAllowedValues = ({ variable }: Props) => {
  const updateParameter = useScenarioStore((state) => state.updateParameter);
  const setValidationError = useScenarioStore(
    (state) => state.setValidationError,
  );
  const clearValidationError = useScenarioStore(
    (state) => state.clearValidationError,
  );
  const { currentTemplate } = useScenarioStore();
  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      data: variable.default,
    },
  });

  // Reset form when variable.default changes (e.g., when a new template is loaded)
  useEffect(() => {
    reset({ data: variable.default });
  }, [variable.default, reset, currentTemplate]);

  // Sync validation errors to the store
  useEffect(() => {
    if (errors.data) {
      setValidationError(variable.id, errors.data.message ?? 'Invalid value');
    } else {
      clearValidationError(variable.id);
    }
  }, [errors.data, variable.id, setValidationError, clearValidationError]);

  // Clean up on unmount
  useEffect(() => {
    return () => clearValidationError(variable.id);
  }, [variable.id, clearValidationError]);

  const handleFormSubmit = (data: { data: number }) => {
    const numValue = normalizeNumericValue(data.data);
    updateParameter(variable.id as keyof ScenarioTemplateParameters, numValue);
  };

  return (
    <form
      onChange={handleSubmit(handleFormSubmit)}
      style={{ display: 'flex', flexDirection: 'row', gap: '1rem' }}
    >
      <Controller
        name="data"
        control={control}
        rules={{
          required: 'Enter value within range',
          validate: (value) => {
            const num = normalizeNumericValue(value);
            return variable.validators.allowedValues.includes(num)
              ? true
              : `Value must be one of: ${variable.validators.allowedValues.join(
                  ', ',
                )}`;
          },
        }}
        render={({ field: { ref, ...props }, fieldState: { error } }) => (
          <TextField
            {...props}
            id={props.name}
            inputRef={ref}
            inputIcon={
              error ? <Icon data={error_filled} title="error" /> : undefined
            }
            helperText={error?.message}
            variant={error ? 'error' : undefined}
            style={{
              minWidth: '60px',
              maxWidth: '140px',
              alignContent: 'center',
            }}
          />
        )}
      />
    </form>
  );
};
